import { useEffect, lazy, Suspense } from "react";
import Lenis from "lenis";
import { BrowserRouter, Routes, Route, useLocation, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import Nav from "@/components/Nav";
import WhatsAppButton from "@/components/WhatsAppButton";
import Footer from "@/components/Footer";

const HomePage = lazy(() => import("@/pages/HomePage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const RegionPage = lazy(() => import("@/pages/RegionPage"));
const BlogPage = lazy(() => import("@/pages/BlogPage"));
const BlogPostPage = lazy(() => import("@/pages/BlogPostPage"));
const ComboPage = lazy(() => import("@/pages/ComboPage"));
const LandingPage = lazy(() => import("@/pages/LandingPage"));
const MuenchenPage = lazy(() => import("@/pages/MuenchenPage"));
const LondonPage = lazy(() => import("@/pages/LondonPage"));
const IntlCityPage = lazy(() => import("@/pages/IntlCityPage"));
const IntlTopicPage = lazy(() => import("@/pages/IntlTopicPage"));
const DeTopicPage = lazy(() => import("@/pages/DeTopicPage"));
const ServicePage = lazy(() => import("@/pages/ServicePage"));
const ImpressumPage = lazy(() => import("@/pages/ImpressumPage"));
const CandidatePage = lazy(() => import("@/pages/CandidatePage"));
const MuenchenImpressumPage = lazy(() => import("@/pages/MuenchenImpressumPage"));
const MuenchenJournalPage = lazy(() => import("@/pages/MuenchenJournalPage"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const PartnerPage = lazy(() => import("@/pages/PartnerPage"));
const DeCityPage = lazy(() => import("@/pages/DeCityPage").then((m) => ({ default: m.DeCityPage })));
const FamilyRegisterPage = lazy(() => import("@/pages/FamilyRegisterPage"));
const NationalPage = lazy(() => import("@/pages/NationalPage"));
const FloridaPage = lazy(() => import("@/pages/FloridaPage"));
const DeCityTopicPage = lazy(() => import("@/pages/DeCityPage").then((m) => ({ default: m.DeCityTopicPage })));

const ComboOrLandingInner = lazy(() =>
  Promise.all([
    import("@/data/landingPages"),
    import("@/pages/LandingPage"),
    import("@/pages/ComboPage"),
    import("@/data/deCities"),
    import("@/data/deCityTopics"),
    import("@/pages/DeCityPage"),
  ]).then(([data, LP, CP, dc, dt, DP]) => {
    const ComboOrLanding = () => {
      const { combo } = useParams();
      if (data.LANDING_PAGES[combo]) return <LP.default />;
      // Deutsche Flat-URLs: {thema}-{stadt} z.B. nanny-agentur-koeln (exakte Keyword-URL)
      for (const citySlug of Object.keys(dc.DE_CITIES)) {
        if (combo.endsWith("-" + citySlug)) {
          const topicSlug = combo.slice(0, -(citySlug.length + 1));
          if (dt.DE_CITY_TOPICS.some((t) => t.slug === topicSlug)) {
            return <DP.DeCityTopicPage cityKey={citySlug} topicSlug={topicSlug} />;
          }
        }
      }
      return <CP.default />;
    };
    return { default: ComboOrLanding };
  })
);

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const GATracker = () => {
  const location = useLocation();
  useEffect(() => {
    if (typeof window.gtag === "function") {
      window.gtag("event", "page_view", {
        page_path: location.pathname + location.search,
        page_title: document.title,
      });
    }
  }, [location.pathname, location.search]);
  return null;
};

const PageFallback = () => (
  <div className="min-h-screen bg-[#FAF7F1]" data-testid="page-loading" />
);

const Chrome = () => {
  const { pathname } = useLocation();
  const bare = ["/muenchen", "/london", "/dubai", "/monaco", "/munich", "/admin", "/partner-werden", "/hamburg", "/frankfurt", "/koeln", "/stuttgart", "/freiburg"].some(
    (p) => pathname === p || pathname.startsWith(p + "/")
  ) || /-(hamburg|frankfurt|koeln|stuttgart|freiburg)$/.test(pathname);
  return (
    <>
      <ScrollToTop />
      <GATracker />
      {!bare && <Nav />}
      <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/regions/:slug" element={<RegionPage />} />
          <Route path="/services/:slug" element={<ServicePage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/impressum" element={<ImpressumPage />} />
          <Route path="/candidates" element={<CandidatePage />} />
          <Route path="/household-staffing" element={<NationalPage slug="household-staffing" />} />
          <Route path="/nanny-agency" element={<NationalPage slug="nanny-agency" />} />
          <Route path="/estate-manager" element={<NationalPage slug="estate-manager" />} />
          <Route path="/live-in-housekeeper" element={<NationalPage slug="live-in-housekeeper" />} />
          <Route path="/domestic-couple" element={<NationalPage slug="domestic-couple" />} />
          <Route path="/butler-agency" element={<NationalPage slug="butler-agency" />} />
          <Route path="/housekeepers" element={<NationalPage slug="housekeepers" />} />
          <Route path="/household-managers" element={<NationalPage slug="household-managers" />} />
          <Route path="/florida" element={<FloridaPage />} />
          <Route path="/elite-household-staffing-agency" element={<NationalPage slug="elite-household-staffing-agency" />} />
          <Route path="/elite-nanny-agency" element={<NationalPage slug="elite-nanny-agency" />} />
          <Route path="/muenchen" element={<MuenchenPage />} />
          <Route path="/muenchen/impressum" element={<MuenchenImpressumPage />} />
          <Route path="/muenchen/journal" element={<MuenchenJournalPage />} />
          <Route path="/muenchen/:topic" element={<DeTopicPage />} />
          <Route path="/london" element={<LondonPage />} />
          <Route path="/dubai" element={<IntlCityPage cityKey="dubai" />} />
          <Route path="/monaco" element={<IntlCityPage cityKey="monaco" />} />
          <Route path="/munich" element={<IntlCityPage cityKey="munich" />} />
          <Route path="/london/:topic" element={<IntlTopicPage cityKey="london" />} />
          <Route path="/dubai/:topic" element={<IntlTopicPage cityKey="dubai" />} />
          <Route path="/monaco/:topic" element={<IntlTopicPage cityKey="monaco" />} />
          <Route path="/munich/:topic" element={<IntlTopicPage cityKey="munich" />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/partner-werden" element={<PartnerPage />} />
          <Route path="/familie-registrieren" element={<FamilyRegisterPage lang="de" />} />
          <Route path="/register-family" element={<FamilyRegisterPage lang="en" />} />
          <Route path="/nanny-agentur-gruenden-hamburg" element={<PartnerPage cityKey="hamburg" />} />
          <Route path="/nanny-agentur-gruenden-frankfurt" element={<PartnerPage cityKey="frankfurt" />} />
          <Route path="/nanny-agentur-gruenden-koeln" element={<PartnerPage cityKey="koeln" />} />
          <Route path="/nanny-agentur-gruenden-stuttgart" element={<PartnerPage cityKey="stuttgart" />} />
          <Route path="/nanny-agentur-gruenden-freiburg" element={<PartnerPage cityKey="freiburg" />} />
          <Route path="/hamburg" element={<DeCityPage cityKey="hamburg" />} />
          <Route path="/frankfurt" element={<DeCityPage cityKey="frankfurt" />} />
          <Route path="/koeln" element={<DeCityPage cityKey="koeln" />} />
          <Route path="/stuttgart" element={<DeCityPage cityKey="stuttgart" />} />
          <Route path="/freiburg" element={<DeCityPage cityKey="freiburg" />} />
          <Route path="/:combo" element={<ComboOrLandingInner />} />
        </Routes>
      </Suspense>
      {!bare && <Footer />}
      <WhatsAppButton />
      <Toaster position="bottom-right" />
    </>
  );
};

function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09 });
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  return (
    <div className="App bg-[#FAF7F1] text-[#33302A]" data-testid="app-root">
      <BrowserRouter>
        <Chrome />
      </BrowserRouter>
    </div>
  );
}

export default App;
